import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, X, AlertTriangle } from "lucide-react";

export default function Budgets() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get("/budgets");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/budgets/${id}`); load();
  };
  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Aylık Limitler</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Bütçeler</h1>
        </div>
        <button data-testid="budget-add-button" onClick={() => setOpen(true)}
                className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2">
          <Plus size={16} /> Yeni Bütçe
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="brutal-card p-8 text-center col-span-full text-sm text-neutral-500">Bütçe eklenmedi.</div>
        )}
        {items.map((b) => {
          const percent = Math.min(b.percent, 100);
          const alert = b.percent >= b.alert_percent;
          const over = b.percent >= 100;
          return (
            <div key={b.id} className="brutal-card p-6" data-testid={`budget-${b.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-caps">{alert && !over ? "Uyarı" : over ? "Aşıldı" : "Sağlıklı"}</div>
                  <h3 className="font-display text-xl font-bold mt-1">{b.category}</h3>
                </div>
                {alert && <AlertTriangle size={18} className={over ? "text-rose-600" : "text-amber-500"} />}
              </div>
              <div className="mt-6">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono-num text-2xl font-semibold">{fmtTRY(b.spent)}</span>
                  <span className="label-caps">/ {fmtTRY(b.monthly_limit)}</span>
                </div>
                <div className="mt-3 h-2 bg-neutral-100 border border-black/10">
                  <div className={`h-full ${over ? "bg-rose-500" : alert ? "bg-amber-500" : "bg-black"}`} style={{ width: `${percent}%` }} />
                </div>
                <div className="flex items-center justify-between mt-2 text-xs text-neutral-500">
                  <span>%{b.percent} kullanıldı</span>
                  <button data-testid={`budget-delete-${b.id}`} onClick={() => remove(b.id)} className="hover:bg-rose-50 p-1"><Trash2 size={12} className="text-rose-600" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {open && <BudgetModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function BudgetModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ category: "", monthly_limit: "", alert_percent: 80 });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/budgets", { ...form, monthly_limit: Number(form.monthly_limit), alert_percent: Number(form.alert_percent) });
      toast.success("Eklendi"); onSaved();
    } catch { toast.error("Hata"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white border border-black w-full max-w-md">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">Yeni Bütçe</h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <F label="Kategori"><input required data-testid="budget-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" placeholder="Kira, Yemek..." /></F>
          <F label="Aylık Limit (₺)"><input required type="number" data-testid="budget-limit" value={form.monthly_limit} onChange={(e) => setForm({ ...form, monthly_limit: e.target.value })} className="input font-mono-num" /></F>
          <F label={`Uyarı Eşiği: %${form.alert_percent}`}>
            <input type="range" min="50" max="100" value={form.alert_percent} onChange={(e) => setForm({ ...form, alert_percent: e.target.value })} className="w-full" />
          </F>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="budget-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Kaydet</button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}
function F({ label, children }) { return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>; }
